const rememberFeedback = (message) => {
    try { sessionStorage.setItem('festival-photo-management-feedback', message); } catch { /* Storage may be disabled by browser privacy settings. */ }
};

const uploadRoots = new WeakSet();

const request = async (url, method, body, signal) => {
    const response = await fetch(url, {
        method, signal, credentials: 'same-origin',
        headers: {
            Accept: 'application/json', 'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!response.headers.get('content-type')?.includes('application/json')) {
        throw new Error('Your session may have expired. Reload this page and sign in again.');
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(Object.values(data.errors ?? {}).flat()[0] || data.message || 'The change could not be saved. Please try again.');
    return data;
};

export const initializePhotoManagement = (modal, { signal, close, pause }) => {
    const root = modal.querySelector('[data-photo-management]');
    if (!root) return;
    const options = root.querySelector('[data-photo-options]');
    const edit = root.querySelector('[data-photo-edit]');
    const remove = root.querySelector('[data-photo-delete]');
    const report = root.querySelector('[data-photo-report]');
    const reportForm = root.querySelector('[data-photo-report-form]');
    const editor = root.querySelector('[data-photo-caption-editor]');
    const confirmation = root.querySelector('[data-photo-delete-confirmation]');
    const status = root.querySelector('[data-photo-management-status]');
    const caption = editor.elements.caption;
    let trigger = null;
    let busy = false;
    const reset = () => { editor.hidden = true; reportForm.hidden = true; confirmation.hidden = true; options.open = false; };
    modal.addEventListener('festival-photo:shown', (event) => {
        trigger = event.detail.trigger;
        reset();
        const reviewOnly = trigger.dataset.photoStatus && trigger.dataset.photoStatus !== 'approved';
        status.textContent = reviewOnly ? (trigger.dataset.photoStatus === 'pending' ? 'This photo is awaiting approval.' : 'This photo is not public.') : '';
        modal.querySelectorAll('[data-photo-modal-tags-section], [data-photo-modal-reaction-form], [data-photo-modal-tab="reactions"], [data-photo-modal-tab="comments"]').forEach(element => { element.hidden = Boolean(reviewOnly); });
        edit.hidden = !trigger.dataset.photoUpdateUrl;
        remove.hidden = !trigger.dataset.photoDeleteUrl;
        report.hidden = !trigger.dataset.photoReportUrl;
        reportForm.reset();
        options.hidden = edit.hidden && remove.hidden && report.hidden;
        const title = modal.querySelector('#festival-photo-modal-title');
        if (trigger.dataset.photoFestivalName) title.textContent = trigger.dataset.photoFestivalName;
    }, { signal });
    root.addEventListener('click', (event) => {
        pause();
        if (busy) return;
        if (event.target.closest('[data-photo-edit]')) {
            reset(); caption.value = trigger.dataset.photoCaption ?? ''; editor.hidden = false; caption.focus();
        } else if (event.target.closest('[data-photo-delete]')) {
            reset(); confirmation.hidden = false; root.querySelector('[data-photo-delete-cancel]').focus();
        } else if (event.target.closest('[data-photo-report]')) {
            reset(); reportForm.hidden = false; reportForm.elements.category.focus();
        } else if (event.target.closest('[data-photo-edit-cancel], [data-photo-delete-cancel], [data-photo-report-cancel]')) {
            reset(); options.querySelector('summary').focus();
        }
    }, { signal });
    const perform = async (action) => {
        if (busy) return;
        busy = true;
        root.querySelectorAll('button').forEach((button) => { button.disabled = true; });
        try { await action(); } catch (error) {
            if (error.name !== 'AbortError') status.textContent = error.message;
        } finally {
            busy = false;
            root.querySelectorAll('button').forEach((button) => { button.disabled = false; });
        }
    };
    editor.addEventListener('submit', (event) => {
        event.preventDefault();
        const selected = trigger;
        perform(async () => {
            const data = await request(selected.dataset.photoUpdateUrl, 'PATCH', { caption: caption.value }, signal);
            document.querySelectorAll('.festival-photo-lightbox-trigger').forEach((item) => {
                if (item.dataset.photoId !== selected.dataset.photoId) return;
                item.dataset.photoCaption = data.photo.caption ?? '';
                const preview = item.matches('.ff-activity-card') ? null : item.querySelector('img');
                if (preview?.alt) preview.alt = data.photo.caption || `Photo from ${item.dataset.photoFestivalName || 'this festival'}`;
            });
            if (trigger !== selected) return;
            modal.querySelector('[data-photo-modal-image]').alt = data.photo.caption || `Festival photo uploaded by ${selected.dataset.photoUploader || 'FestFriends member'}`;
            modal.querySelector('[data-photo-modal-caption]').textContent = data.photo.caption ?? '';
            modal.querySelector('[data-photo-modal-caption-wrapper]')?.classList.toggle('hidden', !data.photo.caption);
            reset(); status.textContent = data.message; options.querySelector('summary').focus();
        });
    }, { signal });
    reportForm.addEventListener('submit', (event) => {
        event.preventDefault();
        const selected = trigger;
        perform(async () => {
            const data = await request(selected.dataset.photoReportUrl, 'POST', { category: reportForm.elements.category.value, details: reportForm.elements.details.value }, signal);
            if (trigger !== selected) return;
            reset(); status.textContent = data.message; options.querySelector('summary').focus();
        });
    }, { signal });
    root.querySelector('[data-photo-delete-confirm]').addEventListener('click', () => {
        const selected = trigger;
        perform(async () => {
            await request(selected.dataset.photoDeleteUrl, 'DELETE', undefined, signal);
            close();
            // Re-fetch server-derived totals, pagination and filtered acknowledgement snapshots.
            rememberFeedback('Photo deleted.');
            window.location.reload();
        });
    }, { signal });
};

const initializeUploads = () => {
    const root = document.querySelector('[data-my-festival-photos]');
    if (!root || uploadRoots.has(root)) return;
    uploadRoots.add(root);
    const controls = root.querySelector('[data-photo-bulk-controls]');
    const selected = () => [...root.querySelectorAll('[data-photo-select]:checked')].map((input) => Number(input.value));
    const confirmation = controls.querySelector('[data-photo-bulk-confirmation]');
    const status = controls.querySelector('[data-photo-bulk-status]');
    let busy = false;
    root.addEventListener('change', (event) => {
        if (event.target.matches('[data-photo-select-all]')) root.querySelectorAll('[data-photo-select]').forEach((input) => { input.checked = event.target.checked; });
        const count = selected().length;
        const total = root.querySelectorAll('[data-photo-select]').length;
        const all = root.querySelector('[data-photo-select-all]');
        all.checked = count > 0 && count === total;
        all.indeterminate = count > 0 && count < total;
        controls.querySelector('[data-photo-selected-count]').textContent = `${count} selected`;
        controls.querySelector('[data-photo-bulk-delete]').disabled = !count;
        confirmation.hidden = true;
    });
    controls.querySelector('[data-photo-bulk-delete]').addEventListener('click', () => {
        controls.querySelector('[data-photo-bulk-confirmation-text]').textContent = `Delete ${selected().length} selected photos? This cannot be undone here.`;
        confirmation.hidden = false;
        controls.querySelector('[data-photo-bulk-cancel]').focus();
    });
    controls.querySelector('[data-photo-bulk-cancel]').addEventListener('click', () => { confirmation.hidden = true; controls.querySelector('[data-photo-bulk-delete]').focus(); });
    controls.querySelector('[data-photo-bulk-confirm]').addEventListener('click', async () => {
        if (busy) return;
        busy = true;
        const ids = selected();
        root.querySelectorAll('input,button').forEach((input) => { input.disabled = true; });
        try {
            const data = await request(controls.dataset.deleteUrl, 'DELETE', { photo_ids: ids });
            rememberFeedback(data.message);
            window.location.reload();
        } catch (error) {
            status.textContent = error.message;
            root.querySelectorAll('input,button').forEach((input) => { input.disabled = false; });
            busy = false;
        }
    });
};

const initialize = () => {
    initializeUploads();
    let message = null;
    try {
        message = sessionStorage.getItem('festival-photo-management-feedback');
        sessionStorage.removeItem('festival-photo-management-feedback');
    } catch { /* Feedback must not prevent photo controls from initializing. */ }
    if (message) {
        const status = document.createElement('p');
        status.setAttribute('role', 'status'); status.className = 'my-4 px-4'; status.textContent = message;
        document.querySelector('main')?.prepend(status);
    }
};
document.addEventListener('livewire:navigated', initialize);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize); else initialize();
